import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { toast } from 'sonner';
import { 
  ArrowLeft, Car, Shield, FileText, CheckCircle, AlertTriangle, 
  Calculator, Printer, DollarSign, Calendar, ChevronRight, Lock,
  Building, Users, Landmark, RefreshCw, Info, Pen, FileCheck, Scale, Download, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  generateAutoSubmissionMemorandum, 
  generateAutoAcceptanceForm,
  generateTokenizedCheckDisclosure
} from '../utils/documentTemplates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

// Treasury-Indexed Auto Obligation Products
const TIAO_PRODUCTS = [
  {
    id: 'tiao-36',
    name: 'TIAO-36 (36-Month)',
    description: '36-month Treasury-indexed auto obligation',
    term: 36,
    referenceRate: 'U.S. Treasury 2-Year CMR',
    baseAPR: 6.50,
    spread: 2.00,
    minAmount: 15000,
    maxAmount: 150000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Flexible Terms']
  },
  {
    id: 'tiao-48',
    name: 'TIAO-48 (48-Month)',
    description: '48-month Treasury-indexed auto obligation',
    term: 48,
    referenceRate: 'U.S. Treasury 3-Year CMR',
    baseAPR: 6.00,
    spread: 1.75,
    minAmount: 20000,
    maxAmount: 200000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lower Monthly']
  },
  {
    id: 'tiao-60',
    name: 'TIAO-60 (60-Month)',
    description: '60-month Treasury-indexed auto obligation',
    term: 60,
    referenceRate: 'U.S. Treasury 5-Year CMR',
    baseAPR: 5.75,
    spread: 1.50,
    minAmount: 25000,
    maxAmount: 250000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Extended Term']
  },
  {
    id: 'tiao-72',
    name: 'TIAO-72 (72-Month)',
    description: '72-month Treasury-indexed auto obligation',
    term: 72,
    referenceRate: 'U.S. Treasury 5-Year CMR',
    baseAPR: 5.50,
    spread: 1.25,
    minAmount: 30000,
    maxAmount: 300000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lowest Payment']
  }
];

const AutoFinancing = () => {
  const { user, token } = useAuth();
  const { account, bethBalance } = useWeb3();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [vehicleAmount, setVehicleAmount] = useState('');
  const [costSheet, setCostSheet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [novations, setNovations] = useState([]);
  const [showNovationForm, setShowNovationForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Form for creating new auto obligation
  const [formData, setFormData] = useState({
    borrower_name: '',
    borrower_wallet: '',
    dealer_name: '',
    dealer_contact: '',
    vehicle_vin: '',
    vehicle_description: '',
    vehicle_price: '',
    down_payment: '',
    term_months: 60,
    beth_collateral: ''
  });
  
  // Novation form for refinancing existing auto loans
  const [novationForm, setNovationForm] = useState({
    original_creditor_name: '',
    original_creditor_contact: '',
    original_account_number: '',
    original_principal: '',
    original_rate: '',
    original_monthly_payment: '',
    original_remaining_balance: '',
    vehicle_vin: '',
    vehicle_description: '',
    settlement_amount: '',
    effective_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchOrders();
    fetchNovations();
  }, [token]);

  useEffect(() => {
    if (account) {
      setFormData(prev => ({ ...prev, borrower_wallet: account }));
    }
  }, [account]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, borrower_name: user.name || user.email }));
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/auto-financing/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };
  
  const fetchNovations = async () => {
    try {
      const res = await fetch(`${API}/auto-financing/novations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNovations(data.agreements || []);
      }
    } catch (error) {
      console.error('Error fetching novations:', error);
    }
  };
  
  const createNovationAgreement = async () => {
    if (!selectedProduct || !costSheet || !novationForm.original_creditor_name) {
      toast.error('Please fill in original creditor details and select a TIAO product');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/auto-financing/novation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...novationForm,
          borrower_name: formData.borrower_name,
          borrower_wallet: formData.borrower_wallet,
          new_principal: parseFloat(costSheet.principal),
          new_indexed_apr: parseFloat(costSheet.indexedAPR),
          new_term_months: selectedProduct.term,
          new_monthly_payment: parseFloat(costSheet.monthlyPayment),
          treasury_index: selectedProduct.referenceRate,
          beth_collateral: parseFloat(costSheet.bethCollateral)
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Novation Agreement created! Ready for signatures.');
        fetchNovations();
        setShowNovationForm(false);
        setActiveTab('novations');
        // Print the novation agreement
        setTimeout(() => printNovationDocument(data.agreement), 500);
      } else {
        toast.error(data.error || 'Failed to create novation agreement');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating novation agreement');
    } finally {
      setLoading(false);
    }
  };
  
  const printNovationDocument = (agreement) => {
    const printContent = generateNovationDocumentHTML(agreement);
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        try { printWindow.print(); } catch (e) { toast.info('Use Ctrl+P to print'); }
      }, 1000);
    }
  };

  const generateCompleteDocumentPackage = (agreement) => {
    toast.info('Generating complete document package...', { duration: 2000 });
    
    // Generate all documents
    const novationHTML = generateNovationDocumentHTML(agreement);
    const memorandumHTML = generateAutoSubmissionMemorandum({
      memorandum_id: `MEM-${agreement?.agreement_id || 'DRAFT'}`,
      agreement_id: agreement?.agreement_id,
      borrower_name: agreement?.borrower_name || formData.borrower_name,
      vehicle_description: agreement?.vehicle_description || formData.vehicle_description,
      vehicle_vin: agreement?.vehicle_vin || formData.vehicle_vin
    });
    const acceptanceFormHTML = generateAutoAcceptanceForm({
      form_id: `ACK-${agreement?.agreement_id || 'DRAFT'}`,
      agreement_id: agreement?.agreement_id,
      borrower_name: agreement?.borrower_name || formData.borrower_name,
      creditor_name: agreement?.original_creditor_name || novationForm.original_creditor_name,
      vehicle_vin: agreement?.vehicle_vin || formData.vehicle_vin,
      settlement_amount: agreement?.settlement_amount || novationForm.settlement_amount
    });
    const checkDisclosureHTML = generateTokenizedCheckDisclosure({
      document_id: `TCD-${agreement?.agreement_id || 'DRAFT'}`,
      borrower_name: agreement?.borrower_name || formData.borrower_name,
      lender_name: agreement?.original_creditor_name || novationForm.original_creditor_name
    });
    
    // Combine into multi-page document
    const fullPackage = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Auto Financing Document Package - ${agreement?.agreement_id || 'DRAFT'}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          body { margin: 0; padding: 0; }
          .page { page-break-after: always; min-height: 100vh; }
          .page:last-child { page-break-after: auto; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <!-- Cover Page -->
        <div class="page" style="display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%); color: white; text-align: center; font-family: Georgia, serif;">
          <div>
            <h1 style="font-size: 32px; letter-spacing: 5px; margin-bottom: 10px;">BETHEREUM</h1>
            <p style="color: #b8860b; font-size: 12px; letter-spacing: 3px;">PRIVATE WEALTH MANAGEMENT</p>
            <div style="width: 100px; height: 2px; background: #b8860b; margin: 30px auto;"></div>
            <h2 style="font-size: 18px; font-weight: normal; margin: 30px 0;">TREASURY-INDEXED AUTO OBLIGATION</h2>
            <h3 style="font-size: 14px; font-weight: normal; color: #22d3ee;">Complete Lender Document Package</h3>
            <div style="margin-top: 50px; padding: 20px; border: 1px solid #b8860b; display: inline-block;">
              <p style="font-size: 11px; margin: 0; color: #ccc;">Document ID</p>
              <p style="font-size: 16px; margin: 5px 0 0 0; font-family: monospace; color: #b8860b;">${agreement?.agreement_id || 'DRAFT'}</p>
            </div>
            <p style="margin-top: 50px; font-size: 10px; color: #999;">
              CONFIDENTIAL - FOR CREDIT REVIEW ONLY
            </p>
          </div>
        </div>
        
        <!-- Table of Contents -->
        <div class="page" style="padding: 60px; font-family: Georgia, serif;">
          <h2 style="text-align: center; font-size: 18px; color: #0a1628; margin-bottom: 40px;">TABLE OF CONTENTS</h2>
          <div style="max-width: 500px; margin: 0 auto;">
            <div style="display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px dotted #ccc;">
              <span>1. Credit Enhancement Submission Memorandum</span>
              <span>3</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px dotted #ccc;">
              <span>2. Auto Loan Novation Agreement</span>
              <span>4</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px dotted #ccc;">
              <span>3. Dealer/Creditor Acceptance Form</span>
              <span>7</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px dotted #ccc;">
              <span>4. Tokenized Check Disclosure Agreement</span>
              <span>8</span>
            </div>
          </div>
          <div style="margin-top: 60px; text-align: center; color: #666; font-size: 11px;">
            <p>This document package contains legally binding agreements.</p>
            <p>Please review all documents carefully before signing.</p>
          </div>
        </div>
        
        <!-- Submission Memorandum -->
        ${memorandumHTML}
        
        <!-- Novation Agreement (extract body content) -->
        <div class="page" style="padding: 30px; font-family: Georgia, serif;">
          ${novationHTML.replace(/<html>|<\/html>|<head>.*?<\/head>|<body>|<\/body>|<!DOCTYPE.*?>/gs, '')}
        </div>
        
        <!-- Acceptance Form -->
        ${acceptanceFormHTML}
        
        <!-- Tokenized Check Disclosure -->
        ${checkDisclosureHTML}
        
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes');
    if (printWindow) {
      printWindow.document.write(fullPackage);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        toast.success('Document package ready for print/save');
      }, 500);
    } else {
      toast.error('Popup blocked. Please allow popups.');
    }
  };
  
  const generateNovationDocumentHTML = (agreement) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Auto Loan Novation Agreement - ${agreement?.agreement_id || 'DRAFT'}</title>
        <style>
          @page { size: letter; margin: 0.6in; }
          body { font-family: 'Georgia', serif; margin: 0; padding: 30px; color: #0a1628; line-height: 1.7; max-width: 850px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 20px; margin-bottom: 25px; position: relative; }
          .header h1 { color: #0a1628; font-size: 26px; margin: 0; letter-spacing: 3px; }
          .header .subtitle { color: #b8860b; font-size: 10px; letter-spacing: 2px; margin-top: 5px; }
          .seal { position: absolute; top: 0; left: 0; width: 50px; height: 50px; border: 2px solid #0a1628; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
          .seal span { font-size: 6px; color: #0a1628; font-weight: bold; }
          .doc-id { position: absolute; top: 5px; right: 5px; font-family: monospace; font-size: 9px; background: #f0f0f0; padding: 4px 8px; border: 1px solid #ccc; }
          .document-title { text-align: center; background: linear-gradient(180deg, #0a1628 0%, #1a2d4a 100%); color: white; padding: 12px; margin: 15px 0; font-size: 13px; letter-spacing: 2px; }
          .section { margin: 18px 0; }
          .section-title { font-size: 11px; color: #0a1628; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; font-weight: bold; border-bottom: 1px solid #b8860b; padding-bottom: 5px; }
          .recital { margin: 12px 0; padding-left: 20px; font-size: 11px; }
          .recital strong { color: #0a1628; }
          .party-box { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 15px 0; }
          .party { padding: 12px; background: #fafafa; border-left: 3px solid #b8860b; }
          .party-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 1px; }
          .party-value { font-size: 11px; color: #0a1628; margin-top: 3px; }
          .terms-list { margin: 15px 0; }
          .terms-list li { margin: 10px 0; font-size: 10px; }
          .highlight-box { background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%); border: 2px solid #b8860b; padding: 15px; margin: 18px 0; text-align: center; }
          .highlight-box .amount { font-size: 22px; font-weight: bold; color: #b8860b; }
          .signature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 35px; }
          .sig-box { border: 1px solid #0a1628; padding: 15px; text-align: center; }
          .sig-line { border-top: 1px solid #0a1628; width: 85%; margin: 40px auto 5px auto; }
          .sig-label { font-size: 9px; text-transform: uppercase; color: #666; }
          .legal-text { font-size: 9px; line-height: 1.6; text-align: justify; background: #f9f9f9; padding: 12px; border: 1px solid #ddd; margin: 15px 0; }
          .warning { background: #fff3cd; border: 1px solid #ffc107; padding: 10px; margin: 15px 0; font-size: 9px; color: #856404; }
          .footer { margin-top: 25px; padding-top: 15px; border-top: 2px solid #0a1628; font-size: 8px; color: #666; text-align: center; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="seal"><span>OFFICIAL</span><span style="font-size:9px">✦</span><span>SEAL</span></div>
          <div class="doc-id">DOC: ${agreement?.agreement_id || 'DRAFT'}</div>
          <h1>BETHEREUM</h1>
          <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
        </div>
        
        <div class="document-title">AUTO LOAN NOVATION & SETTLEMENT AGREEMENT</div>
        
        <div class="warning">
          <strong>⚠️ IMPORTANT:</strong> This is a legally binding agreement for the novation (replacement) of an existing auto loan obligation. 
          All parties must sign for this agreement to be effective. This is NOT a consumer refinance - it is a private contractual arrangement.
        </div>
        
        <div class="section">
          <div class="section-title">PARTIES TO THIS AGREEMENT</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">Borrower / Obligor</div>
              <div class="party-value">${agreement?.borrower_name || formData.borrower_name || '_______________'}</div>
              <div class="party-value" style="font-family: monospace; font-size: 9px;">${agreement?.borrower_wallet || formData.borrower_wallet || '0x...'}</div>
            </div>
            <div class="party">
              <div class="party-label">Original Creditor / Current Lienholder</div>
              <div class="party-value">${agreement?.original_creditor_name || novationForm.original_creditor_name || '_______________'}</div>
              <div class="party-value">${agreement?.original_creditor_contact || novationForm.original_creditor_contact || ''}</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">RECITALS</div>
          <div class="recital"><strong>WHEREAS,</strong> Borrower is currently obligated under an existing auto loan with Original Creditor;</div>
          <div class="recital"><strong>WHEREAS,</strong> the parties desire to novate (replace) said obligation with a Treasury-Indexed Auto Obligation (TIAO);</div>
          <div class="recital"><strong>WHEREAS,</strong> Original Creditor consents to the settlement and release of the existing obligation upon receipt of consideration;</div>
          <div class="recital"><strong>NOW, THEREFORE,</strong> in consideration of the mutual promises herein, the parties agree as follows:</div>
        </div>
        
        <div class="section">
          <div class="section-title">ORIGINAL OBLIGATION DETAILS</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">Account Number</div>
              <div class="party-value">${agreement?.original_account_number || novationForm.original_account_number || '_______________'}</div>
            </div>
            <div class="party">
              <div class="party-label">Original Principal</div>
              <div class="party-value">$${parseFloat(agreement?.original_principal || novationForm.original_principal || 0).toLocaleString()}</div>
            </div>
            <div class="party">
              <div class="party-label">Current Interest Rate</div>
              <div class="party-value">${agreement?.original_rate || novationForm.original_rate || '___'}%</div>
            </div>
            <div class="party">
              <div class="party-label">Remaining Balance</div>
              <div class="party-value">$${parseFloat(agreement?.original_remaining_balance || novationForm.original_remaining_balance || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">VEHICLE COLLATERAL</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">VIN</div>
              <div class="party-value" style="font-family: monospace;">${agreement?.vehicle_vin || novationForm.vehicle_vin || formData.vehicle_vin || '_______________'}</div>
            </div>
            <div class="party">
              <div class="party-label">Vehicle Description</div>
              <div class="party-value">${agreement?.vehicle_description || novationForm.vehicle_description || formData.vehicle_description || '_______________'}</div>
            </div>
          </div>
        </div>
        
        <div class="highlight-box">
          <div style="font-size: 10px; color: #666; margin-bottom: 5px;">SETTLEMENT / PAYOFF AMOUNT</div>
          <div class="amount">$${parseFloat(agreement?.settlement_amount || novationForm.settlement_amount || costSheet?.principal || 0).toLocaleString()}</div>
          <div style="font-size: 10px; margin-top: 5px;">To be paid to Original Creditor upon execution</div>
        </div>
        
        <div class="section">
          <div class="section-title">NEW TREASURY-INDEXED AUTO OBLIGATION (TIAO)</div>
          <div class="party-box">
            <div class="party">
              <div class="party-label">New Principal</div>
              <div class="party-value">$${parseFloat(agreement?.new_principal || costSheet?.principal || 0).toLocaleString()}</div>
            </div>
            <div class="party">
              <div class="party-label">Indexed APR</div>
              <div class="party-value">${agreement?.new_indexed_apr || costSheet?.indexedAPR || '___'}%</div>
            </div>
            <div class="party">
              <div class="party-label">Term</div>
              <div class="party-value">${agreement?.new_term_months || selectedProduct?.term || '___'} months</div>
            </div>
            <div class="party">
              <div class="party-label">New Monthly Payment</div>
              <div class="party-value" style="color: #b8860b; font-weight: bold;">$${parseFloat(agreement?.new_monthly_payment || costSheet?.monthlyPayment || 0).toLocaleString()}</div>
            </div>
            <div class="party">
              <div class="party-label">Treasury Index Reference</div>
              <div class="party-value">${agreement?.treasury_index || selectedProduct?.referenceRate || 'U.S. Treasury CMR'}</div>
            </div>
            <div class="party">
              <div class="party-label">BETH Collateral Enhancement</div>
              <div class="party-value">${parseFloat(agreement?.beth_collateral || costSheet?.bethCollateral || 0).toLocaleString()} BETH</div>
            </div>
          </div>
        </div>
        
        <div class="legal-text">
          <strong>TERMS AND CONDITIONS:</strong><br/><br/>
          1. <strong>NOVATION:</strong> Upon execution and settlement, the Original Obligation is FULLY DISCHARGED and replaced by the TIAO.<br/><br/>
          2. <strong>RELEASE:</strong> Original Creditor releases Borrower from all claims related to the Original Obligation upon receipt of Settlement Amount.<br/><br/>
          3. <strong>LIEN TRANSFER:</strong> Original Creditor shall release its security interest; new lien shall be recorded in favor of the SPV/Trust administering the TIAO.<br/><br/>
          4. <strong>PAS PROTECTION:</strong> A Private Accord & Satisfaction (PAS) credit enhancement backs performance of the new obligation.<br/><br/>
          5. <strong>GOVERNING LAW:</strong> This Agreement is governed by the laws of the State of Delaware, USA.
        </div>
        
        <div class="signature-grid">
          <div class="sig-box">
            <div class="sig-label">Borrower Signature</div>
            <div class="sig-line"></div>
            <div style="font-size: 9px;">${agreement?.borrower_name || formData.borrower_name || '_______________'}</div>
            <div style="font-size: 9px; margin-top: 5px;">Date: _______________</div>
          </div>
          <div class="sig-box">
            <div class="sig-label">Original Creditor Consent</div>
            <div class="sig-line"></div>
            <div style="font-size: 9px;">${agreement?.original_creditor_name || novationForm.original_creditor_name || '_______________'}</div>
            <div style="font-size: 9px; margin-top: 5px;">Date: _______________</div>
          </div>
        </div>
        
        <div class="footer">
          <p>🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
          <p style="font-family: monospace;">BETH Token: ${BETH_TOKEN}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({ ...prev, term_months: product.term }));
    if (vehicleAmount) {
      calculateCostSheet(product, vehicleAmount);
    }
  };

  const handleAmountChange = (value) => {
    setVehicleAmount(value);
    if (selectedProduct && value) {
      calculateCostSheet(selectedProduct, value);
    } else {
      setCostSheet(null);
    }
  };

  const calculateCostSheet = (product, amount) => {
    const principal = parseFloat(amount);
    if (isNaN(principal) || principal <= 0) {
      setCostSheet(null);
      return;
    }

    const monthlyRate = product.baseAPR / 100 / 12;
    const numPayments = product.term;
    
    // Monthly payment calculation (amortization formula)
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                          (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const totalPayments = monthlyPayment * numPayments;
    const totalIndexAdjustment = totalPayments - principal;
    
    // Fee structure (fixed, not performance-based)
    const structuringFee = principal * 0.02; // 2%
    const pasEnhancementFee = principal * 0.01; // 1%
    const adminFee = 500; // Flat fee
    const settlementFee = 250; // Flat fee
    const totalFees = structuringFee + pasEnhancementFee + adminFee + settlementFee;
    
    // BETH collateral (10% of principal)
    const bethCollateral = principal * 0.1; // At $1.00 per BETH

    setCostSheet({
      principal: principal.toFixed(2),
      term: product.term,
      indexedAPR: product.baseAPR.toFixed(2),
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayments: totalPayments.toFixed(2),
      totalIndexAdjustment: totalIndexAdjustment.toFixed(2),
      structuringFee: structuringFee.toFixed(2),
      pasEnhancementFee: pasEnhancementFee.toFixed(2),
      adminFee: adminFee.toFixed(2),
      settlementFee: settlementFee.toFixed(2),
      totalFees: totalFees.toFixed(2),
      bethCollateral: bethCollateral.toFixed(0),
      collateralRatio: 10
    });
    
    setFormData(prev => ({ 
      ...prev, 
      vehicle_price: amount,
      beth_collateral: bethCollateral.toFixed(0)
    }));
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !costSheet) {
      toast.error('Please select a product and enter vehicle price');
      return;
    }
    
    if (!formData.vehicle_vin || !formData.dealer_name) {
      toast.error('Please fill in vehicle VIN and dealer information');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/auto-financing/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          ...formData,
          principal_amount: parseFloat(costSheet.principal),
          indexed_apr: parseFloat(costSheet.indexedAPR),
          monthly_payment: parseFloat(costSheet.monthlyPayment),
          total_payments: parseFloat(costSheet.totalPayments),
          total_fees: parseFloat(costSheet.totalFees),
          beth_collateral: parseFloat(costSheet.bethCollateral)
        })
      });

      const data = await res.json();
      if (data.success) {
        toast.success('Auto Obligation created successfully!');
        fetchOrders();
        setActiveTab('orders');
        // Print the document
        setTimeout(() => printAutoDocument(data.order), 500);
      } else {
        toast.error(data.error || 'Failed to create auto obligation');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating auto obligation');
    } finally {
      setLoading(false);
    }
  };

  const printAutoDocument = (order) => {
    const printContent = generateAutoDocumentHTML(order);
    const printWindow = window.open('', '_blank', 'width=900,height=800,scrollbars=yes');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        try {
          printWindow.print();
          toast.success('Document opened for printing');
        } catch (e) {
          toast.info('Document opened. Use Ctrl+P to print.');
        }
      }, 1000);
    } else {
      toast.error('Popup blocked. Please allow popups for this site.');
    }
  };

  const generateAutoDocumentHTML = (order) => {
    const instrumentNo = order?.order_id || `TIAO-${Date.now().toString(36).toUpperCase()}`;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Treasury-Indexed Auto Obligation - ${instrumentNo}</title>
        <style>
          @page { size: letter; margin: 0.5in; }
          body { 
            font-family: 'Georgia', serif; 
            margin: 0;
            padding: 30px;
            color: #0a1628;
            line-height: 1.6;
            max-width: 850px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            border-bottom: 3px double #b8860b;
            padding-bottom: 20px;
            margin-bottom: 25px;
            position: relative;
          }
          .header h1 { color: #0a1628; font-size: 28px; margin: 0; letter-spacing: 4px; }
          .header .subtitle { color: #b8860b; font-size: 10px; letter-spacing: 2px; margin-top: 5px; }
          .seal {
            position: absolute;
            top: 0;
            left: 0;
            width: 55px;
            height: 55px;
            border: 2px solid #0a1628;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .seal span { font-size: 7px; color: #0a1628; font-weight: bold; line-height: 1.2; }
          .instrument-number {
            position: absolute;
            top: 5px;
            right: 5px;
            font-family: 'Courier New', monospace;
            font-size: 9px;
            background: #f0f0f0;
            padding: 4px 8px;
            border: 1px solid #ccc;
          }
          .document-title {
            text-align: center;
            background: linear-gradient(180deg, #0a1628 0%, #1a2d4a 100%);
            color: white;
            padding: 12px;
            margin: 15px 0;
            font-size: 14px;
            letter-spacing: 2px;
          }
          .section {
            margin: 15px 0;
            padding: 12px;
            background: #fafafa;
            border-left: 4px solid #b8860b;
          }
          .section-title {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
            font-weight: bold;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            border-bottom: 1px dotted #ddd;
            font-size: 11px;
          }
          .row:last-child { border-bottom: none; }
          .row .label { color: #666; }
          .row .value { font-weight: bold; color: #0a1628; }
          .highlight-box {
            background: linear-gradient(135deg, #f8f4e8 0%, #fefefe 100%);
            border: 2px solid #b8860b;
            padding: 15px;
            margin: 15px 0;
            text-align: center;
          }
          .highlight-box .amount {
            font-size: 24px;
            font-weight: bold;
            color: #b8860b;
          }
          .legal-text { 
            font-size: 9px; 
            line-height: 1.6; 
            text-align: justify; 
            background: #f9f9f9;
            padding: 15px;
            border: 1px solid #ddd;
            margin: 15px 0;
          }
          .signature-section {
            display: flex;
            gap: 20px;
            margin-top: 30px;
          }
          .sig-box {
            flex: 1;
            border: 1px solid #0a1628;
            padding: 15px;
            text-align: center;
          }
          .sig-line {
            border-top: 1px solid #0a1628;
            width: 80%;
            margin: 35px auto 5px auto;
          }
          .footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 2px solid #0a1628;
            font-size: 8px;
            color: #666;
            text-align: center;
          }
          .warning-box {
            background: #fff3cd;
            border: 1px solid #ffc107;
            padding: 10px;
            margin: 15px 0;
            font-size: 9px;
            color: #856404;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="seal">
            <span>OFFICIAL</span>
            <span style="font-size: 10px;">✦</span>
            <span>SEAL</span>
          </div>
          <div class="instrument-number">INSTRUMENT: ${instrumentNo}</div>
          <h1>BETHEREUM</h1>
          <p class="subtitle">PRIVATE WEALTH MANAGEMENT</p>
        </div>
        
        <div class="document-title">TREASURY-INDEXED AUTO OBLIGATION (TIAO)</div>
        
        <div class="warning-box">
          <strong>⚠️ IMPORTANT DISCLOSURE:</strong> This is a PRIVATE CREDIT OBLIGATION, NOT a consumer auto loan. 
          This obligation is not issued, guaranteed, or insured by any government agency. Treasury rates are used 
          as a reference index only. Platform does not act as a lender.
        </div>
        
        <div class="section">
          <div class="section-title">BORROWER / PURCHASER INFORMATION</div>
          <div class="row"><span class="label">Name</span><span class="value">${order?.borrower_name || formData.borrower_name || '_______________'}</span></div>
          <div class="row"><span class="label">Wallet Address</span><span class="value" style="font-family: monospace; font-size: 9px;">${order?.borrower_wallet || formData.borrower_wallet || '0x...'}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">DEALER / SELLER INFORMATION</div>
          <div class="row"><span class="label">Dealer Name</span><span class="value">${order?.dealer_name || formData.dealer_name || '_______________'}</span></div>
          <div class="row"><span class="label">Contact</span><span class="value">${order?.dealer_contact || formData.dealer_contact || '_______________'}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">VEHICLE INFORMATION</div>
          <div class="row"><span class="label">VIN</span><span class="value" style="font-family: monospace;">${order?.vehicle_vin || formData.vehicle_vin || '_______________'}</span></div>
          <div class="row"><span class="label">Description</span><span class="value">${order?.vehicle_description || formData.vehicle_description || '_______________'}</span></div>
          <div class="row"><span class="label">Purchase Price</span><span class="value">$${parseFloat(order?.principal_amount || costSheet?.principal || 0).toLocaleString()}</span></div>
        </div>
        
        <div class="highlight-box">
          <div style="font-size: 10px; color: #666; margin-bottom: 5px;">PRINCIPAL OBLIGATION AMOUNT</div>
          <div class="amount">$${parseFloat(order?.principal_amount || costSheet?.principal || 0).toLocaleString()}</div>
          <div style="font-size: 11px; margin-top: 5px;">
            Term: ${order?.term_months || formData.term_months} months | Indexed APR: ${order?.indexed_apr || costSheet?.indexedAPR}%
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">PAYMENT SCHEDULE</div>
          <div class="row"><span class="label">Monthly Payment</span><span class="value">$${parseFloat(order?.monthly_payment || costSheet?.monthlyPayment || 0).toLocaleString()}</span></div>
          <div class="row"><span class="label">Number of Payments</span><span class="value">${order?.term_months || formData.term_months}</span></div>
          <div class="row"><span class="label">Total of Payments</span><span class="value">$${parseFloat(order?.total_payments || costSheet?.totalPayments || 0).toLocaleString()}</span></div>
          <div class="row"><span class="label">Index Reference</span><span class="value">${selectedProduct?.referenceRate || 'U.S. Treasury CMR'}</span></div>
        </div>
        
        <div class="section" style="background: linear-gradient(135deg, #f0f7ff 0%, #fefefe 100%);">
          <div class="section-title">CREDIT ENHANCEMENT (PAS BACKED)</div>
          <div class="row"><span class="label">BETH Collateral Required</span><span class="value">${parseFloat(order?.beth_collateral || costSheet?.bethCollateral || 0).toLocaleString()} BETH</span></div>
          <div class="row"><span class="label">Collateralization Ratio</span><span class="value">${costSheet?.collateralRatio || 10}%</span></div>
          <div class="row"><span class="label">PAS Coverage</span><span class="value">Performance Protection</span></div>
        </div>
        
        <div class="legal-text">
          <strong>TERMS AND CONDITIONS:</strong><br/><br/>
          1. <strong>Nature of Obligation:</strong> This is a private credit obligation, NOT a consumer auto loan. The Borrower 
          acknowledges they are entering into a private contractual arrangement.<br/><br/>
          2. <strong>Treasury Index Reference:</strong> Payment adjustments, if any, are indexed to publicly available U.S. Treasury 
          benchmark rates for reference purposes only. This obligation is not issued or backed by the U.S. Government.<br/><br/>
          3. <strong>PAS Credit Enhancement:</strong> A Private Accord & Satisfaction (PAS) agreement backs performance of this 
          obligation. PAS covers missed payment protection and performance shortfalls up to a capped liability amount.<br/><br/>
          4. <strong>Security Interest:</strong> Borrower grants a security interest in the vehicle. An SPV or trust may be listed 
          as lienholder. A UCC-1 Financing Statement may be filed under Article 9 of the UCC.<br/><br/>
          5. <strong>Platform Role:</strong> Bethereum acts as a structuring and administration platform, NOT as a lender. 
          All fees are for platform services, documentation, and credit enhancement availability.<br/><br/>
          6. <strong>Governing Law:</strong> This obligation is governed by the laws of the State of Delaware, USA.
        </div>
        
        <div class="signature-section">
          <div class="sig-box">
            <div style="font-size: 9px; text-transform: uppercase;">Borrower Signature</div>
            <div class="sig-line"></div>
            <div style="font-size: 9px;">${order?.borrower_name || formData.borrower_name || '_______________'}</div>
            <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
          </div>
          <div class="sig-box">
            <div style="font-size: 9px; text-transform: uppercase;">Dealer / Seller Acceptance</div>
            <div class="sig-line"></div>
            <div style="font-size: 9px;">${order?.dealer_name || formData.dealer_name || '_______________'}</div>
            <div style="font-size: 9px; margin-top: 5px;">Date: _____________</div>
          </div>
        </div>
        
        <div class="footer">
          <p>🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET</p>
          <p style="font-family: monospace;">BETH Token: ${BETH_TOKEN}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="auto-financing-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                DASHBOARD
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide flex items-center gap-2">
                <Car className="w-5 h-5 text-[#b8860b]" />
                Auto Financing Structure
              </h1>
              <p className="text-xs text-gray-500">Treasury-Indexed Auto Obligations (TIAO) with PAS Credit Enhancement</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {account && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Connected Wallet</p>
                <p className="text-sm text-white font-mono">{account.slice(0, 8)}...{account.slice(-6)}</p>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={fetchOrders}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 border-b border-yellow-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 text-yellow-200 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>IMPORTANT:</strong> This is a PRIVATE CREDIT structure, NOT a consumer auto loan. 
              No banking license required. Outside CFPB/TILA jurisdiction. Platform acts as structuring agent, not lender.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="overview-tab"
          >
            <Info className="w-4 h-4 mr-2" />
            HOW IT WORKS
          </Button>
          <Button
            variant={activeTab === 'products' ? 'default' : 'outline'}
            onClick={() => setActiveTab('products')}
            className={activeTab === 'products' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="products-tab"
          >
            <Car className="w-4 h-4 mr-2" />
            TIAO PRODUCTS
          </Button>
          <Button
            variant={activeTab === 'calculator' ? 'default' : 'outline'}
            onClick={() => setActiveTab('calculator')}
            className={activeTab === 'calculator' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="calculator-tab"
          >
            <Calculator className="w-4 h-4 mr-2" />
            CALCULATOR
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('orders')}
            className={activeTab === 'orders' ? 'bg-[#b8860b] hover:bg-[#9a7209]' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="orders-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            MY OBLIGATIONS ({orders.length})
          </Button>
          <Button
            variant={activeTab === 'novations' ? 'default' : 'outline'}
            onClick={() => setActiveTab('novations')}
            className={activeTab === 'novations' ? 'bg-purple-600 hover:bg-purple-700' : 'border-[#1a2d4a] text-gray-400'}
            data-testid="novations-tab"
          >
            <Scale className="w-4 h-4 mr-2" />
            NOVATION ({novations.length})
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Car className="w-5 h-5 text-[#b8860b]" />
                  What is a Treasury-Indexed Auto Obligation (TIAO)?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 mb-4">
                  A TIAO is a <strong className="text-[#b8860b]">private credit obligation</strong> to acquire or refinance a vehicle, 
                  backed by a <strong>PAS credit enhancement</strong> and indexed to Treasury benchmarks. 
                  This keeps transactions outside state lending licenses, CFPB, TILA, and banking law.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                    <h4 className="text-white font-medium mb-1">What You ARE Doing</h4>
                    <p className="text-xs text-gray-400">Issuing a private credit obligation with consideration flowing to seller/lender</p>
                  </div>
                  <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
                    <h4 className="text-white font-medium mb-1">What You ARE NOT Doing</h4>
                    <p className="text-xs text-gray-400">NOT issuing a consumer auto loan or acting as a bank</p>
                  </div>
                  <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                    <Shield className="w-6 h-6 text-blue-400 mb-2" />
                    <h4 className="text-white font-medium mb-1">Protection</h4>
                    <p className="text-xs text-gray-400">PAS credit enhancement backs performance, not payment</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transaction Flow */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">Step-by-Step Transaction Flow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: 'Borrower Onboarding', desc: 'KYC/AML, accreditation acknowledgment, disclosure acceptance' },
                    { step: 2, title: 'Auto Purchase Agreement', desc: 'Select vehicle, get dealer invoice, confirm VIN and terms' },
                    { step: 3, title: 'Issue TIAO', desc: 'Create Treasury-indexed obligation with payment schedule' },
                    { step: 4, title: 'PAS Credit Enhancement', desc: 'PAS backs performance with missed payment protection' },
                    { step: 5, title: 'Tokenized Check (Optional)', desc: 'Conditional consideration evidence, not legal tender' },
                    { step: 6, title: 'Settlement & Delivery', desc: 'Funds delivered, vehicle transferred to borrower' },
                    { step: 7, title: 'Title & Security Interest', desc: 'SPV/Trust as lienholder, UCC-1 filed under Article 9' }
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-4 p-3 bg-[#0a1628] rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-[#b8860b] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{item.title}</h4>
                        <p className="text-xs text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Best For */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">Best Use Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['New & Used Vehicles', 'Dealer Sales', 'Private Sales', 'Sub-Prime Borrowers (with PAS)', 'Refinance Existing Loans', 'Fleet Vehicles'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TIAO_PRODUCTS.map((product) => (
              <Card 
                key={product.id}
                className={`bg-[#0d1d35] border-[#1a2d4a] hover:border-[#b8860b] transition-colors cursor-pointer ${selectedProduct?.id === product.id ? 'border-[#b8860b] border-2' : ''}`}
                onClick={() => handleProductSelect(product)}
                data-testid={`product-${product.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-white text-base">{product.name}</CardTitle>
                      <CardDescription className="text-gray-400 mt-1 text-xs">{product.description}</CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#b8860b]">{product.baseAPR}%</div>
                      <div className="text-xs text-gray-500">Indexed APR</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-[#0a1628] rounded">
                      <Calendar className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                      <div className="text-white font-bold">{product.term}</div>
                      <div className="text-xs text-gray-500">Months</div>
                    </div>
                    <div className="text-center p-3 bg-[#0a1628] rounded">
                      <DollarSign className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                      <div className="text-white font-bold text-sm">{formatCurrency(product.minAmount)}</div>
                      <div className="text-xs text-gray-500">Min</div>
                    </div>
                    <div className="text-center p-3 bg-[#0a1628] rounded">
                      <Landmark className="w-4 h-4 mx-auto text-[#b8860b] mb-1" />
                      <div className="text-white font-bold text-xs">{product.referenceRate.replace('U.S. Treasury ', '').replace(' CMR', '')}</div>
                      <div className="text-xs text-gray-500">Index</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-[#1a2d4a] text-gray-300 rounded">
                        <CheckCircle className="w-3 h-3 inline mr-1 text-green-400" />
                        {feature}
                      </span>
                    ))}
                  </div>
                  <Button 
                    className="w-full mt-4 bg-[#b8860b] hover:bg-[#9a7209]"
                    onClick={(e) => { e.stopPropagation(); handleProductSelect(product); setActiveTab('calculator'); }}
                  >
                    Calculate Terms <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Car className="w-5 h-5 text-[#b8860b]" />
                  Vehicle & Term Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Select Term</label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {TIAO_PRODUCTS.map((product) => (
                      <Button
                        key={product.id}
                        variant={selectedProduct?.id === product.id ? 'default' : 'outline'}
                        className={selectedProduct?.id === product.id ? 'bg-[#b8860b]' : 'border-[#1a2d4a] text-gray-400'}
                        onClick={() => handleProductSelect(product)}
                        size="sm"
                      >
                        {product.term}mo
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Vehicle Price (USD)</label>
                  <Input
                    type="number"
                    value={vehicleAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="Enter vehicle price"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="vehicle-price-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Vehicle VIN</label>
                  <Input
                    value={formData.vehicle_vin}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicle_vin: e.target.value }))}
                    placeholder="Enter 17-character VIN"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white font-mono"
                    maxLength={17}
                    data-testid="vehicle-vin-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Vehicle Description</label>
                  <Input
                    value={formData.vehicle_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicle_description: e.target.value }))}
                    placeholder="Year, Make, Model, Trim"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="vehicle-desc-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Dealer / Seller Name</label>
                  <Input
                    value={formData.dealer_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, dealer_name: e.target.value }))}
                    placeholder="Dealership or seller name"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="dealer-name-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Dealer Contact</label>
                  <Input
                    value={formData.dealer_contact}
                    onChange={(e) => setFormData(prev => ({ ...prev, dealer_contact: e.target.value }))}
                    placeholder="Phone or email"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#b8860b]" />
                  Cost Sheet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costSheet ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-[#b8860b]/30">
                      <div className="text-xs text-[#b8860b] uppercase tracking-wider mb-2">Monthly Payment</div>
                      <div className="text-3xl font-bold text-white">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</div>
                      <div className="text-sm text-gray-400 mt-1">for {costSheet.term} months at {costSheet.indexedAPR}% APR</div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Vehicle Price</span>
                        <span className="text-white">${parseFloat(costSheet.principal).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Total of Payments</span>
                        <span className="text-white">${parseFloat(costSheet.totalPayments).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Total Index Adjustment</span>
                        <span className="text-gray-400">${parseFloat(costSheet.totalIndexAdjustment).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <div className="text-xs text-blue-400 mb-2 font-medium">PLATFORM FEES (Fixed, Non-Performance)</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Structuring Fee (2%)</span>
                          <span className="text-white">${parseFloat(costSheet.structuringFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">PAS Enhancement Fee (1%)</span>
                          <span className="text-white">${parseFloat(costSheet.pasEnhancementFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Admin Fee</span>
                          <span className="text-white">${costSheet.adminFee}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Settlement Fee</span>
                          <span className="text-white">${costSheet.settlementFee}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-blue-700/50">
                          <span className="text-gray-400">Total Fees</span>
                          <span className="text-[#b8860b] font-bold">${parseFloat(costSheet.totalFees).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[#0a1628] rounded-lg">
                      <div className="flex items-center gap-2 text-xs text-[#b8860b] mb-2">
                        <Lock className="w-4 h-4" />
                        CREDIT ENHANCEMENT (BETH)
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">BETH Required</span>
                        <span className="text-white">{parseFloat(costSheet.bethCollateral).toLocaleString()} BETH</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-400">Collateralization</span>
                        <span className="text-white">{costSheet.collateralRatio}%</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                        onClick={handleCreateOrder}
                        disabled={loading || !formData.vehicle_vin || !formData.dealer_name}
                        data-testid="create-tiao-btn"
                      >
                        {loading ? 'Creating...' : 'Create TIAO'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-[#1a2d4a] text-[#b8860b]"
                        onClick={() => printAutoDocument(null)}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Select a term and enter vehicle price to see payment details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#b8860b]" />
                My Auto Obligations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.order_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-white font-medium">{order.product_name}</h4>
                          <p className="text-xs text-gray-500 font-mono">{order.order_id}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          order.status === 'active' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status?.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Vehicle</p>
                          <p className="text-white">{order.vehicle_description || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Principal</p>
                          <p className="text-white">${parseFloat(order.principal_amount || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Monthly</p>
                          <p className="text-[#b8860b]">${parseFloat(order.monthly_payment || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Term</p>
                          <p className="text-white">{order.term_months} mo</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => printAutoDocument(order)}>
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedOrder(order); setShowNovationForm(true); setActiveTab('novations'); }}>
                          <Scale className="w-4 h-4 mr-1" /> Refinance
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Car className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No auto obligations yet</p>
                  <p className="text-sm mt-2">Use the calculator tab to create your first TIAO</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Novation Tab */}
        {activeTab === 'novations' && (
          <div className="space-y-6">
            {/* Novation Info Card */}
            <Card className="bg-gradient-to-r from-purple-900/30 to-[#0d1d35] border-purple-500/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Scale className="w-6 h-6 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium">Auto Loan Novation Agreement</h3>
                    <p className="text-sm text-gray-400">
                      Refinance an existing auto loan by replacing it with a Treasury-Indexed Auto Obligation (TIAO).
                      The original creditor must consent to release their lien.
                    </p>
                  </div>
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => setShowNovationForm(true)}
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    New Novation
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Novation Form */}
            {showNovationForm && (
              <Card className="bg-[#0d1d35] border-purple-500/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Scale className="w-5 h-5 text-purple-400" />
                    Create Auto Loan Novation Agreement
                  </CardTitle>
                  <CardDescription>
                    Replace an existing auto loan with a new TIAO
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Original Loan Details */}
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center">1</span>
                      Original Loan Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400">Original Creditor / Lender Name *</label>
                        <Input
                          value={novationForm.original_creditor_name}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_creditor_name: e.target.value }))}
                          placeholder="Current auto loan holder"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Contact</label>
                        <Input
                          value={novationForm.original_creditor_contact}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_creditor_contact: e.target.value }))}
                          placeholder="Phone or email"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Account Number</label>
                        <Input
                          value={novationForm.original_account_number}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_account_number: e.target.value }))}
                          placeholder="Loan account number"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Current Interest Rate %</label>
                        <Input
                          type="number"
                          step="0.25"
                          value={novationForm.original_rate}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_rate: e.target.value }))}
                          placeholder="e.g., 8.5"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Remaining Balance / Payoff Amount *</label>
                        <Input
                          type="number"
                          value={novationForm.original_remaining_balance}
                          onChange={(e) => {
                            setNovationForm(prev => ({ ...prev, original_remaining_balance: e.target.value, settlement_amount: e.target.value }));
                            handleAmountChange(e.target.value);
                          }}
                          placeholder="Amount to pay off"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Current Monthly Payment</label>
                        <Input
                          type="number"
                          value={novationForm.original_monthly_payment}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, original_monthly_payment: e.target.value }))}
                          placeholder="Current payment"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Vehicle Details */}
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center">2</span>
                      Vehicle Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400">Vehicle VIN *</label>
                        <Input
                          value={novationForm.vehicle_vin || formData.vehicle_vin}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, vehicle_vin: e.target.value }))}
                          placeholder="17-character VIN"
                          maxLength={17}
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Vehicle Description</label>
                        <Input
                          value={novationForm.vehicle_description || formData.vehicle_description}
                          onChange={(e) => setNovationForm(prev => ({ ...prev, vehicle_description: e.target.value }))}
                          placeholder="Year, Make, Model"
                          className="mt-1 bg-[#0a1628] border-[#1a2d4a] text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Step 3: New TIAO Terms */}
                  <div className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center">3</span>
                      New TIAO Terms
                    </h4>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {TIAO_PRODUCTS.map((product) => (
                        <Button
                          key={product.id}
                          variant={selectedProduct?.id === product.id ? 'default' : 'outline'}
                          className={selectedProduct?.id === product.id ? 'bg-purple-600' : 'border-[#1a2d4a] text-gray-400'}
                          onClick={() => handleProductSelect(product)}
                          size="sm"
                        >
                          {product.term}mo @ {product.baseAPR}%
                        </Button>
                      ))}
                    </div>
                    
                    {costSheet && (
                      <div className="grid grid-cols-3 gap-4 p-3 bg-purple-900/20 rounded-lg">
                        <div className="text-center">
                          <p className="text-xs text-gray-400">New Monthly</p>
                          <p className="text-lg font-bold text-purple-400">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-400">Indexed APR</p>
                          <p className="text-lg font-bold text-white">{costSheet.indexedAPR}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-400">BETH Required</p>
                          <p className="text-lg font-bold text-[#b8860b]">{parseFloat(costSheet.bethCollateral).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                    
                    {novationForm.original_monthly_payment && costSheet && (
                      <div className="mt-3 p-2 bg-green-900/20 border border-green-700/50 rounded text-center">
                        <p className="text-green-400 text-sm">
                          Potential Monthly Savings: <strong>${(parseFloat(novationForm.original_monthly_payment) - parseFloat(costSheet.monthlyPayment)).toFixed(2)}</strong>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                      onClick={createNovationAgreement}
                      disabled={loading || !novationForm.original_creditor_name || !costSheet}
                    >
                      {loading ? 'Creating...' : (
                        <>
                          <FileCheck className="w-4 h-4 mr-2" />
                          Create Novation Agreement
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-[#1a2d4a] text-gray-400"
                      onClick={() => setShowNovationForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Existing Novations */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">My Novation Agreements</CardTitle>
              </CardHeader>
              <CardContent>
                {novations.length > 0 ? (
                  <div className="space-y-4">
                    {novations.map((novation) => (
                      <div key={novation.agreement_id} className="p-4 bg-[#0a1628] rounded-lg border border-[#1a2d4a]">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="text-white font-medium">Novation Agreement</h4>
                            <p className="text-xs text-gray-500 font-mono">{novation.agreement_id}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded ${
                            novation.status === 'fully_executed' ? 'bg-green-500/20 text-green-400' :
                            novation.status === 'pending_signatures' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {novation.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Original Creditor</p>
                            <p className="text-white">{novation.original_creditor_name}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Payoff Amount</p>
                            <p className="text-white">${parseFloat(novation.settlement_amount || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">New Monthly</p>
                            <p className="text-purple-400">${parseFloat(novation.new_monthly_payment || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">New Term</p>
                            <p className="text-white">{novation.new_term_months} mo</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => printNovationDocument(novation)}>
                            <Printer className="w-4 h-4 mr-1" /> Print Agreement
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => generateCompleteDocumentPackage(novation)} className="border-[#b8860b] text-[#b8860b]">
                            <Package className="w-4 h-4 mr-1" /> Full Package
                          </Button>
                          {novation.status === 'pending_signatures' && (
                            <Button 
                              size="sm" 
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => {
                                toast.info('E-Signature workflow: Document ready for signing. In production, this would open DocuSign or similar e-signature service.');
                                // Mark as signed (placeholder for actual e-signature integration)
                                setTimeout(() => {
                                  toast.success('Signature captured! Document marked as signed.');
                                }, 1500);
                              }}
                            >
                              <Pen className="w-4 h-4 mr-1" /> Sign
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Scale className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No novation agreements yet</p>
                    <p className="text-sm mt-2">Click &quot;New Novation&quot; to refinance an existing auto loan</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AutoFinancing;
